import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { dedupById } from "@/lib/utils";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, BarChart, Bar, Cell, PieChart, Pie } from "recharts";
import StatusBadge from "@/components/StatusBadge";
import { TrendUpIcon, WarningIcon, CheckCircleIcon, TimerIcon, GaugeIcon, PackageIcon, FactoryIcon, ClockIcon, MagnifyingGlassIcon, ReceiptXIcon, BellIcon, ShieldCheckIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import TwoLineDocNo from "@/components/TwoLineDocNo";

// Feb 2026 · HS Code Audit tile — lists every ACTIVE SO / ESO line item that
// still has a blank tariff heading so the sales team can fix it before the
// invoice / commercial-invoice hits print. Auto-refreshes with the rest of
// the dashboard polling cycle.
function HsCodeAuditCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/hs-code-audit");
      setData(data);
    } catch (e) { /* silent — dashboard tile is best-effort */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  const total = data?.total ?? 0;
  return (
    <div className="tactical-card p-4" data-testid="hs-audit-card">
      <div className="flex items-start justify-between mb-3">
        <div className="overline">HS Code Audit</div>
        <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${total > 0 ? "bg-amber-500/15" : "bg-emerald-500/15"}`}>
          <ReceiptXIcon size={14} weight="bold" className={total > 0 ? "text-amber-400" : "text-emerald-400"} />
        </div>
      </div>
      <div className={`font-mono text-3xl font-semibold ${total > 0 ? "text-amber-400" : "text-emerald-400"}`} data-testid="hs-audit-count">
        {total}
      </div>
      <div className="text-xs text-zinc-500 mt-1">
        {total === 0
          ? "Every active line carries an HS Code."
          : <><span className="font-mono">{data?.so_count || 0}</span> SO · <span className="font-mono">{data?.eso_count || 0}</span> ESO lines missing</>}
      </div>
      {total > 0 && (
        <button
          data-testid="hs-audit-toggle"
          onClick={() => setOpen(o => !o)}
          className="mt-2 text-[11px] text-amber-300 hover:text-amber-200 uppercase tracking-widest"
        >
          {open ? "Hide" : "Show"} affected lines
        </button>
      )}
      {open && total > 0 && (
        <div className="mt-3 max-h-56 overflow-y-auto border border-[var(--border)] rounded-sm divide-y divide-[var(--border)]">
          {(data?.orphans || []).map((o, idx) => (
            <Link
              key={`${o.kind}-${o.doc_id}-${idx}`}
              to={o.kind === "ESO" ? "/exports" : "/sales-orders"}
              data-testid={`hs-audit-row-${idx}`}
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 hover:bg-[var(--bg-2)]/40 text-[11px]"
            >
              <span className={`inline-block px-1 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest ${o.kind === "ESO" ? "bg-sky-500/15 text-sky-300" : "bg-blue-500/15 text-blue-300"}`}>
                {o.kind}
              </span>
              <span className="font-mono text-white flex-shrink-0">{o.doc_no}</span>
              <span className="font-mono text-[var(--accent)] truncate flex-1" title={o.article_no}>{o.article_no || "—"}</span>
              <span className="font-mono text-zinc-500 text-[10px] flex-shrink-0">{o.delivery_date || ""}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// Feb 2026 · Critical Stock Alerts tile — red, clickable, glows when
// count > 0. Drills into `/inventory/low-stock` for the procurement queue.
function CriticalStockAlertsTile({ count = 0 }) {
  const hot = count > 0;
  return (
    <Link
      to="/inventory/low-stock"
      data-testid="kpi-critical-stock"
      title={hot ? `${count} raw material(s) below minimum stock — click to review` : "All raw materials are at or above minimum stock"}
      className={`tactical-card p-4 block transition group ${hot
        ? "border-red-500/40 hover:border-red-500 hover:bg-red-500/5 shadow-[0_0_24px_-12px_rgba(239,68,68,0.45)]"
        : "hover:border-[var(--accent)]/50"}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`overline ${hot ? "text-red-300" : ""}`}>Critical Stock Alerts</div>
        <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${hot ? "bg-red-500/20 group-hover:bg-red-500/30" : "bg-[var(--bg-2)]"}`}>
          <WarningIcon size={14} weight="fill" className={hot ? "text-red-400" : "text-zinc-500"} />
        </div>
      </div>
      <div className={`font-mono text-3xl font-semibold ${hot ? "text-red-400" : "text-emerald-400"}`}>
        {count}
      </div>
      <div className={`text-xs mt-1 ${hot ? "text-red-300/80" : "text-zinc-500"}`}>
        {hot ? "below minimum · click to restock" : "all materials healthy"}
      </div>
    </Link>
  );
}

const KPI = ({ label, value, unit, sub, icon: Icon, color = "text-white", testId }) => (
  <div className="tactical-card p-4" data-testid={testId}>
    <div className="flex items-start justify-between mb-3">
      <div className="overline">{label}</div>
      {Icon && (
        <div className="w-7 h-7 rounded-sm bg-[var(--bg-2)] flex items-center justify-center">
          <Icon size={14} className={color} weight="bold" />
        </div>
      )}
    </div>
    <div className={`font-mono text-3xl font-semibold ${color}`}>
      {value}
      {unit && <span className="text-sm text-zinc-500 ml-1">{unit}</span>}
    </div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </div>
);

const MiniStat = ({ label, value, unit, sub, color = "text-white", testId }) => (
  <div className="p-3 rounded-sm bg-[var(--bg-2)]/50 border border-[var(--border)]" data-testid={testId}>
    <div className="overline text-[9px] mb-1">{label}</div>
    <div className={`font-mono text-xl font-semibold ${color}`}>
      {value}
      {unit && <span className="text-[10px] text-zinc-500 ml-1">{unit}</span>}
    </div>
    {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

function ArticleKpiCard() {
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/article-kpi");
        setArticles(data.articles || []);
      } catch (e) { console.warn("article-kpi load failed", e); }
    })();
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return articles.slice(0, 20);
    return articles.filter(a => a.article_no.toLowerCase().includes(s)).slice(0, 20);
  }, [q, articles]);

  const jumpToOrder = (article_no) => {
    try { sessionStorage.setItem("mes_pl_article_filter", article_no); } catch (err) { /* noop */ }
  };

  return (
    <div className="tactical-card p-5" data-testid="article-kpi-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline">Article KPI Lookup</div>
          <div className="font-display text-lg font-bold text-white">Cross-Factory Snapshot</div>
          <div className="text-xs text-zinc-500 mt-0.5">Total · Dispatched · Pending · Open Orders — for any article</div>
        </div>
        <div className="w-9 h-9 rounded-sm bg-[var(--accent)]/10 flex items-center justify-center">
          <PackageIcon size={18} className="text-[var(--accent)]" weight="bold" />
        </div>
      </div>

      <div className="relative" ref={wrapRef}>
        <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10" />
        <input
          data-testid="article-kpi-search"
          value={selected ? selected.article_no : q}
          onChange={e => { setQ(e.target.value); setSelected(null); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={articles.length === 0 ? "Loading articles…" : `Search across ${articles.length} article numbers…`}
          className="w-full h-10 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
        />
        {open && filtered.length > 0 && !selected && (
          <div className="absolute top-full mt-1 left-0 right-0 max-h-64 overflow-y-auto bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-2xl z-20" data-testid="article-kpi-list">
            {filtered.map(a => (
              <button
                key={a.article_no}
                onClick={() => { setSelected(a); setQ(""); setOpen(false); }}
                data-testid={`article-kpi-opt-${a.article_no}`}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[var(--bg-2)] text-xs border-b border-[var(--border)] last:border-0"
              >
                <span className="font-mono truncate flex-1 text-[var(--accent)]">{a.article_no}</span>
                <span className="font-mono text-zinc-400 text-[10px]">{a.total_qty.toLocaleString("en-IN", { maximumFractionDigits: 0 })} m · {a.open_orders + a.closed_orders} SO</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <MiniStat label="Total Qty" value={selected.total_qty.toLocaleString("en-IN", { maximumFractionDigits: 0 })} unit="m" testId="article-kpi-total" />
            <MiniStat label="Dispatched" value={selected.disp_qty.toLocaleString("en-IN", { maximumFractionDigits: 0 })} unit="m" color="text-emerald-400" testId="article-kpi-disp" />
            <MiniStat label="Pending" value={selected.pending_qty.toLocaleString("en-IN", { maximumFractionDigits: 0 })} unit="m" color="text-amber-400" testId="article-kpi-pending" />
            <MiniStat label="Open Orders" value={selected.open_orders} sub={`${selected.closed_orders} closed`} testId="article-kpi-orders" />
          </div>
          {selected.total_qty > 0 && (
            <div className="mt-3 h-1.5 bg-[var(--bg-2)] rounded-sm overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (selected.disp_qty / selected.total_qty) * 100)}%` }} />
            </div>
          )}
          {selected.customers.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="overline">Customers:</span>
              {selected.customers.map(c => (
                <span key={c} className="font-mono text-[11px] text-zinc-400">{c}</span>
              ))}
            </div>
          )}
          {selected.orders.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1 text-xs">
              <span className="overline mr-1">Orders:</span>
              {selected.orders.map(on => (
                <Link
                  key={on}
                  to="/sales-orders"
                  onClick={() => jumpToOrder(selected.article_no)}
                  data-testid={`article-kpi-order-${on}`}
                  title={`Open Sales Orders (filtered by ${selected.article_no})`}
                  className="font-mono text-[11px] px-1.5 py-0.5 rounded-sm bg-[var(--accent)]/10 text-[var(--accent)] hover:bg-[var(--accent)]/25 hover:underline"
                >
                  {on}
                </Link>
              ))}
            </div>
          )}
          <div className="mt-3 flex justify-end">
            <button data-testid="article-kpi-clear" onClick={() => { setSelected(null); setQ(""); }} className="text-xs text-zinc-400 hover:text-white">Clear</button>
          </div>
        </>
      ) : (
        <div className="mt-4 text-xs text-zinc-500 italic">Type or pick an article number to see live totals across the factory.</div>
      )}
    </div>
  );
}


export default function Dashboard() {
  const { user } = useAuth();
  // Sep 03, 2026 · Silence 403 noise — `/approvals/count` is
  // Administrator-only. Skip the fetch for every other role so operator
  // consoles stay clean.
  const isAdmin = (user?.role || "") === "Administrator";
  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState([]);
  const [pva, setPva] = useState([]);
  const [orders, setOrders] = useState([]);
  // Feb 2026 · Inventory KPI pulled separately so the "Critical Stock
  // Alerts" tile stays fresh alongside the main OEE/Availability KPI row.
  const [invKpis, setInvKpis] = useState(null);
  // Feb 2026 · Authorization Alert widget — pending-approval counts by
  // doc type plus the latest 5 notifications for the signed-in admin.
  const [approvals, setApprovals] = useState({ SO: 0, ESO: 0, PO: 0, WO: 0, total: 0 });

  const load = async () => {
    try {
      const [k, t, p, o, ik, ac] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/production-trend?days=7"),
        api.get("/dashboard/planned-vs-actual"),
        api.get("/sales-orders/"),
        api.get("/dashboard/inventory-kpis").catch(() => ({ data: null })),
        isAdmin
          ? api.get("/approvals/count").catch(() => ({ data: {} }))
          : Promise.resolve({ data: {} }),
      ]);
      setKpis(k.data);
      setTrend(t.data);
      setPva(p.data);
      setOrders(dedupById(o.data));
      setInvKpis(ik.data);
      setApprovals({ ...(ac.data || {}), total: (ac.data?.SO || 0) + (ac.data?.ESO || 0) + (ac.data?.PO || 0) + (ac.data?.WO || 0) });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!kpis) return <div className="p-8 text-zinc-400">Loading…</div>;

  const machineStatusData = [
    { name: "Running", value: kpis.machines.running, color: "#10B981" },
    { name: "Idle", value: kpis.machines.idle, color: "#F59E0B" },
    { name: "Breakdown", value: kpis.machines.breakdown, color: "#EF4444" },
    { name: "Maintenance", value: kpis.machines.maintenance, color: "#3B82F6" },
    { name: "Power Off", value: kpis.machines.power_off, color: "#6B7280" },
  ];

  const criticalOrders = orders.filter(o => (o.priority === "Critical" || o.priority === "High") && !["Completed", "Closed", "Cancelled"].includes(o.status)).slice(0, 6);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Executive Dashboard</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white">Plant Overview</h1>
        </div>
        <div className="text-right">
          <div className="overline mb-1">Last Sync</div>
          <div className="font-mono text-sm text-zinc-400">{new Date().toLocaleTimeString()}</div>
        </div>
      </div>

      {/* Mar 3, 2026 · Recent Notifications tile removed per operator
          request — the Pending Approvals card carries all the actionable
          signal; the notification list was noise. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dash-approvals-widget">
        <Link to="/approvals" className="tactical-card p-4 lg:col-span-1 border-l-2 border-l-amber-500 hover:border-l-amber-400 transition group">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon size={16} weight="bold" className="text-amber-400" />
              <div className="overline text-amber-300">Pending Approvals</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`font-mono text-3xl font-black ${approvals.total > 0 ? "text-amber-300" : "text-zinc-500"}`}>{approvals.total}</span>
              {approvals.total > 0 && <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75"></span><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500"></span></span>}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[["SO", approvals.SO], ["ESO", approvals.ESO], ["PO", approvals.PO], ["WO", approvals.WO]].map(([k, v]) => (
              <div key={k} className="border border-[var(--border)] rounded-sm py-1.5">
                <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
                <div className={`font-mono text-lg ${v > 0 ? "text-white" : "text-zinc-600"}`}>{v || 0}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 group-hover:text-[var(--accent)]">Open Approval Queue →</div>
        </Link>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <KPI label="OEE" value={kpis.oee_pct} unit="%" icon={GaugeIcon} color="text-[var(--accent)]" testId="kpi-oee" />
        <KPI label="Availability" value={kpis.availability_pct} unit="%" icon={CheckCircleIcon} color="text-emerald-400" testId="kpi-availability" />
        <KPI label="Performance" value={kpis.performance_pct} unit="%" icon={TrendUpIcon} color="text-amber-400" testId="kpi-performance" />
        <KPI label="Quality Rate" value={kpis.production.quality_rate_pct} unit="%" icon={CheckCircleIcon} color="text-emerald-400" testId="kpi-quality" />
        <KPI label="Machine Util." value={kpis.machines.utilization_pct} unit="%" icon={FactoryIcon} color="text-white" testId="kpi-utilization" />
        <KPI label="Today Output" value={kpis.production.today_meters} unit="m" icon={PackageIcon} color="text-white" testId="kpi-today-production" />
      </div>

      {/* Second row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI label="Total Machines" value={kpis.machines.total} icon={FactoryIcon} testId="kpi-total-machines" />
        <KPI label="Running" value={kpis.machines.running} icon={CheckCircleIcon} color="text-emerald-400" testId="kpi-running" />
        <KPI label="Breakdown" value={kpis.machines.breakdown} icon={WarningIcon} color="text-red-400" testId="kpi-breakdown" />
        <KPI label="Delayed Orders" value={kpis.orders.delayed} icon={ClockIcon} color="text-red-400" testId="kpi-delayed" sub={`${kpis.orders.critical} critical`} />
        {/* Feb 2026 · On-Time Delivery % — completion_date ≤ delivery_date over
            every SO that has a completion timestamp. Tooltip surfaces the raw
            counts so planners can spot small-sample skew. */}
        <KPI
          label="On-Time Delivery"
          value={kpis.orders.on_time_pct ?? 0}
          unit="%"
          icon={CheckCircleIcon}
          color={(kpis.orders.on_time_pct ?? 0) >= 90 ? "text-emerald-400" : (kpis.orders.on_time_pct ?? 0) >= 70 ? "text-amber-400" : "text-red-400"}
          testId="kpi-on-time-delivery"
          sub={`${kpis.orders.on_time_count ?? 0} on-time · ${kpis.orders.late_count ?? 0} late`}
        />
        {/* Feb 2026 · Critical Stock Alerts — count of Raw Materials whose
            current_stock < min_stock. Red warning tile · clickable, opens
            the Low Stock Alerts drill-in page. */}
        <CriticalStockAlertsTile count={invKpis?.low_stock_count ?? 0} />
      </div>

      {/* Article KPI lookup */}
      <ArticleKpiCard />

      {/* HS Code Audit (Feb 2026) — surfaces active SO / ESO lines that still
          need a tariff heading before invoicing. */}
      <HsCodeAuditCard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Trend */}
        <div className="tactical-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">7-day Production Trend</div>
              <div className="font-display text-lg font-bold text-white">Daily Output (meters)</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend}>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#71717A" fontSize={11} tick={{ fill: "#A1A1AA" }} />
              <YAxis stroke="#71717A" fontSize={11} tick={{ fill: "#A1A1AA" }} />
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A", borderRadius: 4 }} labelStyle={{ color: "#FAFAFA" }} />
              <Line type="monotone" dataKey="produced" stroke="#007AFF" strokeWidth={2} dot={{ fill: "#007AFF", r: 3 }} />
              <Line type="monotone" dataKey="rejected" stroke="#EF4444" strokeWidth={2} dot={{ fill: "#EF4444", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Machine status donut */}
        <div className="tactical-card p-5">
          <div className="overline mb-1">Machine Status</div>
          <div className="font-display text-lg font-bold text-white mb-4">Live Distribution</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={machineStatusData} innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                {machineStatusData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A", borderRadius: 4 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {machineStatusData.map(s => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                  <span className="text-zinc-400">{s.name}</span>
                </div>
                <span className="font-mono text-white">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Critical orders */}
      <div className="tactical-card">
        <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="overline">Priority Focus</div>
            <div className="font-display text-lg font-bold text-white">Critical & High Priority Orders</div>
          </div>
          <Link to="/sales-orders" className="text-xs text-[var(--accent)] hover:underline" data-testid="view-all-orders">View all →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-5 py-3 font-medium">Order #</th>
                <th className="text-left px-5 py-3 font-medium">PO</th>
                <th className="text-left px-5 py-3 font-medium">Priority</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-right px-5 py-3 font-medium">Qty</th>
                <th className="text-right px-5 py-3 font-medium">Produced</th>
                <th className="text-left px-5 py-3 font-medium">Delivery</th>
              </tr>
            </thead>
            <tbody>
              {criticalOrders.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-500">No critical orders</td></tr>
              )}
              {criticalOrders.map(o => {
                const totalQty = o.items?.reduce((a, b) => a + (b.quantity || 0), 0);
                const totalProd = o.items?.reduce((a, b) => a + (b.produced || 0), 0);
                return (
                  <tr key={o.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`order-row-${o.order_no}`}>
                    <td className="px-5 py-3 text-right"><TwoLineDocNo value={o.order_no} className="text-white" /></td>
                    <td className="px-5 py-3 text-zinc-400">{o.po_number || "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={o.priority} /></td>
                    <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                    <td className="px-5 py-3 text-right font-mono text-white">{totalQty}</td>
                    <td className="px-5 py-3 text-right font-mono text-zinc-400">{totalProd}</td>
                    <td className="px-5 py-3 font-mono text-zinc-400">{o.delivery_date}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Planned vs Actual snapshot */}
      <div className="tactical-card">
        <div className="p-5 border-b border-[var(--border)] flex items-end justify-between gap-3">
          <div>
            <div className="overline">Adherence</div>
            <div className="font-display text-lg font-bold text-white">Planned vs Actual</div>
          </div>
          {/* Mar 2026 · Totals strip — computed over the FULL `pva`
              array (not the visible slice) so a scrolled-off machine
              still contributes to the header sums. Any drift between
              this pill and the row-total below would indicate a lost
              row and can be caught at a glance. */}
          {pva.length > 0 && (() => {
            const totalPlanned = pva.reduce((a, r) => a + Number(r.planned_qty || 0), 0);
            const totalActual  = pva.reduce((a, r) => a + Number(r.actual_qty  || 0), 0);
            const fmt = (n) => Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
            return (
              <div className="flex items-center gap-3 text-[11px] font-mono text-zinc-400" data-testid="pva-header-totals">
                <span title="All machines with a plan for today">
                  <span className="uppercase tracking-widest text-[9px] text-zinc-500 mr-1">Machines</span>
                  <span className="text-white" data-testid="pva-machine-count">{pva.length}</span>
                </span>
                <span className="text-zinc-700">·</span>
                <span title="Sum of planned meters across every machine (not just the visible rows)">
                  <span className="uppercase tracking-widest text-[9px] text-zinc-500 mr-1">Total Planned</span>
                  <span className="text-[var(--accent)] tabular-nums" data-testid="pva-total-planned">{fmt(totalPlanned)}</span>
                  <span className="text-zinc-500 ml-1">m</span>
                </span>
                <span className="text-zinc-700">·</span>
                <span title="Sum of actual meters logged so far">
                  <span className="uppercase tracking-widest text-[9px] text-zinc-500 mr-1">Total Actual</span>
                  <span className="text-emerald-400 tabular-nums" data-testid="pva-total-actual">{fmt(totalActual)}</span>
                  <span className="text-zinc-500 ml-1">m</span>
                </span>
              </div>
            );
          })()}
        </div>
        {/* Mar 2026 · Scroll Support — cap the viewport at ~24rem and
            enable overflow-y so long lists don't push the page. */}
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-24rem)]" data-testid="pva-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-5 py-3 font-medium">Order</th>
                <th className="text-left px-5 py-3 font-medium">Customer</th>
                <th className="text-left px-5 py-3 font-medium">Product Code</th>
                <th className="text-left px-5 py-3 font-medium">Machine</th>
                <th className="text-right px-5 py-3 font-medium">Planned</th>
                <th className="text-right px-5 py-3 font-medium">Actual</th>
                <th className="text-right px-5 py-3 font-medium">Var %</th>
                <th className="text-right px-5 py-3 font-medium">Complete</th>
                <th className="text-left px-5 py-3 font-medium">Forecast</th>
                <th className="text-left px-5 py-3 font-medium">Customer Delivery</th>
                <th className="text-left px-5 py-3 font-medium" title="Work Order delivery date = SO delivery − standard buffer. This is the true target date of completion the shop-floor must beat.">Target Date (WO)</th>
                <th className="text-left px-5 py-3 font-medium">Schedule Status</th>
              </tr>
            </thead>
            <tbody>
              {/* Mar 2026 · Removed `.slice(0, 8)` cap — every machine
                  with a plan for today is now rendered. Scroll container
                  above handles long lists without breaking the layout. */}
              {pva.map((r, i) => (
                <tr key={`pva-${r.order_no || "x"}-${r.machine_code || "x"}-${i}`} className="border-b border-[var(--border)]" data-testid={`pva-row-${i}`}>
                  <td className="px-5 py-3 text-right"><TwoLineDocNo value={r.order_no} className="text-white" /></td>
                  <td className="px-5 py-3 text-zinc-400">{r.customer}</td>
                  <td className="px-5 py-3 font-mono text-cyan-300 text-xs truncate max-w-[220px]" title={r.product_code || r.item_code || ""} data-testid={`pva-product-code-${i}`}>{r.product_code || r.item_code || "—"}</td>
                  <td className="px-5 py-3 font-mono text-zinc-400">{r.machine_code}</td>
                  <td className="px-5 py-3 text-right font-mono text-white">{r.planned_qty}</td>
                  <td className="px-5 py-3 text-right font-mono text-white">{r.actual_qty}</td>
                  <td className={`px-5 py-3 text-right font-mono ${r.status === "green" ? "text-emerald-400" : r.status === "yellow" ? "text-amber-400" : "text-red-400"}`}>
                    {r.variance_pct}%
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-16 h-1.5 bg-[var(--bg-2)] rounded-sm overflow-hidden">
                        <div
                          className={`h-full ${r.status === "green" ? "bg-emerald-500" : r.status === "yellow" ? "bg-amber-500" : "bg-red-500"}`}
                          style={{ width: `${r.completion_pct}%` }}
                        />
                      </div>
                      <span className="font-mono text-xs text-zinc-400 w-10 text-right">{r.completion_pct}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-zinc-400 text-xs">{r.forecast_finish}</td>
                  <td className="px-5 py-3 font-mono text-zinc-300 text-xs" data-testid={`pva-customer-delivery-${i}`}>{r.line_delivery || "—"}</td>
                  <td className="px-5 py-3 font-mono text-amber-300 text-xs" data-testid={`pva-target-date-${i}`} title={`Work Order delivery date · Customer delivery − ${r.post_loom_buffer_days || 4}d buffer`}>{r.wo_delivery_date || r.target_date || r.weaving_deadline || "—"}</td>
                  <td className="px-5 py-3" data-testid={`pva-schedule-status-${i}`}>
                    {r.schedule_status === "delayed" ? (
                      <span
                        data-testid={`pva-delayed-${i}`}
                        className="text-[10px] font-bold px-2 py-1 rounded-sm bg-red-500/20 text-red-300 border border-red-400/50 uppercase tracking-widest"
                        title={`Planned finish ${r.planned_finish} exceeds WO target date ${r.wo_delivery_date || r.target_date} (customer ${r.line_delivery} − ${r.post_loom_buffer_days || 4}d buffer) by ${r.days_late}d`}
                      >Delayed · +{r.days_late}d</span>
                    ) : r.schedule_status === "on_track" ? (
                      <span
                        data-testid={`pva-on-track-${i}`}
                        className="text-[10px] font-bold px-2 py-1 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-400/40 uppercase tracking-widest"
                        title={`Planned finish ${r.planned_finish} ≤ WO target date ${r.wo_delivery_date || r.target_date}`}
                      >On-Track</span>
                    ) : (
                      <span className="text-[10px] text-zinc-600">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {pva.length === 0 && (
                <tr><td colSpan={12} className="text-center py-8 text-zinc-500">No production plans yet</td></tr>
              )}
            </tbody>
            {/* Mar 2026 · In-table totals footer — sticky at the bottom
                of the scroll container so operators see the full-list
                sums no matter how far they scroll. Matches the header
                pill values as a self-check. */}
            {pva.length > 0 && (() => {
              const totalPlanned = pva.reduce((a, r) => a + Number(r.planned_qty || 0), 0);
              const totalActual  = pva.reduce((a, r) => a + Number(r.actual_qty  || 0), 0);
              const fmt = (n) => Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
              return (
                <tfoot className="sticky bottom-0 bg-[var(--bg-2)]/95 backdrop-blur">
                  <tr className="border-t-2 border-[var(--accent)]/40 text-xs" data-testid="pva-totals-row">
                    <td colSpan={4} className="px-5 py-2.5 text-right uppercase tracking-widest text-zinc-500">Totals · {pva.length} machine{pva.length === 1 ? "" : "s"}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-[var(--accent)] tabular-nums font-semibold">{fmt(totalPlanned)} m</td>
                    <td className="px-5 py-2.5 text-right font-mono text-emerald-400 tabular-nums font-semibold">{fmt(totalActual)} m</td>
                    <td colSpan={6}></td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      </div>

      {/* Feb 2026 · Top 5 Most Delayed — leadership shortcut to prioritise
          machine time on the biggest slippages. Sorted by days_late desc
          and re-fetches every 10s via the polling loop above. */}
      {(() => {
        const delayed = (pva || [])
          .filter(r => r.schedule_status === "delayed")
          .sort((a, b) => (b.days_late || 0) - (a.days_late || 0))
          .slice(0, 5);
        return (
          <div className="tactical-card" data-testid="top-delayed-widget">
            <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <div className="overline">Management Alert</div>
                <div className="font-display text-lg font-bold text-white">Top 5 Most Delayed Items</div>
              </div>
              <span className="text-[11px] font-mono text-zinc-500">refreshes every 10s</span>
            </div>
            {delayed.length === 0 ? (
              <div className="p-6 text-center text-emerald-300 text-sm" data-testid="top-delayed-empty">
                All plans on track — nothing overdue right now.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                    <th className="text-left px-5 py-3 font-medium">Order</th>
                    <th className="text-left px-5 py-3 font-medium">Customer</th>
                    <th className="text-left px-5 py-3 font-medium">Item</th>
                    <th className="text-left px-5 py-3 font-medium">Machine</th>
                    <th className="text-left px-5 py-3 font-medium">Customer Delivery</th>
                    <th className="text-left px-5 py-3 font-medium">Weaving Deadline</th>
                    <th className="text-left px-5 py-3 font-medium">Planned Finish</th>
                    <th className="text-right px-5 py-3 font-medium">Days Late</th>
                  </tr>
                </thead>
                <tbody>
                  {delayed.map((r, i) => (
                    <tr key={`delayed-${r.plan_id}`} className="border-b border-[var(--border)]" data-testid={`top-delayed-row-${i}`}>
                      <td className="px-5 py-3 text-right"><TwoLineDocNo value={r.order_no} className="text-white" /></td>
                      <td className="px-5 py-3 text-zinc-400">{r.customer || "—"}</td>
                      <td className="px-5 py-3 font-mono text-zinc-300 text-xs">{r.item_code}</td>
                      <td className="px-5 py-3 font-mono text-zinc-400">{r.machine_code}</td>
                      <td className="px-5 py-3 font-mono text-zinc-300 text-xs">{r.line_delivery || "—"}</td>
                      <td className="px-5 py-3 font-mono text-amber-300 text-xs">{r.weaving_deadline || "—"}</td>
                      <td className="px-5 py-3 font-mono text-red-300 text-xs">{r.planned_finish || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <span
                          data-testid={`top-delayed-days-${i}`}
                          className="font-mono font-bold text-red-300 bg-red-500/15 border border-red-400/40 px-2 py-1 rounded-sm text-xs"
                        >+{r.days_late}d</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}
    </div>
  );
}
